// GrnApi
import { externalpostApi, externalputApi } from "./externalApis";
import {
  GRN_API_01,
  GRN_API_05,
  GRN_API_06,
  GRN_API_07,
  GRN_API_08,
  GRN_API_15,
  GRN_API_16,
} from "../api/ApiModel";
import {
  CON_BUSINESS_TYPECODE,
  CON_DEFAULT_DATE,
  CON_GRN_STATUS_HOLD,
  CON_GRN_STATUS_SAVED,
  CON_GRN_STATUS_SUBMIT,
} from "../utils/sys/Constants";
import { FormatDate, FormatDateShort } from "../utils/common/DateUtils";
import { itemData } from "../components/grid/GridDataGrn";
import {
  AnyForNow,
  ApiResponse,
  ProcessGrnApiParams,
  SubmitGrnDataParams,
  UpdateGrnBasicDataParams,
  UpdateGrnProductDataParams,
  UpdateGrnDocsDataParams,
} from "../features/grn/types";

/** True if value is a file path or URL (not base64). API expects path in docpathjson and base64 in bytarr. */
function isPathOrUrl(value: unknown): boolean {
  if (value == null || typeof value !== "string") return false;
  const s = String(value).trim();
  if (s.startsWith("http://") || s.startsWith("https://")) return true;
  if (s.length < 80) return true;
  return !/^[A-Za-z0-9+/=]+$/.test(s);
}

/** For submit payload: use docpathjson for path/URL and bytarr for base64 so API validation passes. */
function docPayloadField(
  fileData: string
): { docpathjson: string; bytarr: string } {
  const data = (fileData || "").trim();
  if (!data) return { docpathjson: "", bytarr: "" };
  if (isPathOrUrl(data)) return { docpathjson: data, bytarr: "" };
  return { docpathjson: "", bytarr: data };
}

export async function createGrnApi(payload: AnyForNow): Promise<AnyForNow> {
  try {
    const response = await externalpostApi(GRN_API_01, payload);
    return response;
  } catch (error) {
    throw error;
  }
}

export async function submitGrnData({
  grnDetailsForm,
  selectedSupplier,
  onSuccess,
  onError,
  attachments,
  resattachments,
  USERNAME,
  COMPCODE,
  RESTCODE,
  STAFFMAPLOCODE,
  grncode,
  grnData,
  charges,
  addedCharges,
  supcharges,
  sts = CON_GRN_STATUS_SAVED,
  trsts = CON_GRN_STATUS_SAVED,
  reason,
  remark,
  calSummary,
  processResponse,
}: SubmitGrnDataParams): Promise<void> {
  try {
    const products = (grnData?.products || [])
      .filter((item) => item.isAdded === true) //  Only include added items
      .map((item) => {
        console.log("Debugging calSummary:", processResponse); // Log calSummary
        console.log("Debugging dtCalProducts:", calSummary[0]?.dtCalProducts); // Log dtCalProducts
        console.log("Debugging item.supproductcode:", item.supproductcode); // Log supproductcode

        const processed = calSummary[0]?.dtCalProducts?.find(
          (p) => String(p.supproductcode) === String(item.supproductcode)
        );
        console.log("processed:", processed); // Log the result of find

        // Normalize itemAttachments to an array (empty if none)
        const attachments = Array.isArray(item.itemAttachments)
          ? item.itemAttachments
          : item.itemAttachments?.attachments || [];
        // const resattachments = item.itemAttachments?.resattachments || [];
        // const doctypecode = String(item?.itemAttachments?.doctypecode || "");
        const remark = Array.isArray(item.itemAttachments)
          ? ""
          : item.itemAttachments?.remark || "";

        return {
          supproductcode: Number(item.supproductcode) || 0,
          poqty: Number(item.poqty) || 0,
          receivedqty: Number(item.receivedqty) || 0,
          rejectedqty: Number(item.rejectItem?.returnedqty) || 0, //new
          acceptedqty: Number(item.acceptedqty) || 0,
          freeqty: Number(item.freeqty) || 0,
          billqty: Number(processed?.billqty) || 0,
          totalqty: Number(processed?.totalqty) || 0, //new
          pendingpoqty:
            Number(item.pendingpoqty) || Number(processed?.pendingpoqty) || 0, //new
          unitprice: Number(item.unitprice) || 0,
          unitpricewithfree: Number(processed?.unitpricewithfree) || 0, //new
          unitdistribution: Number(processed?.unitdistribution) || 0, //new
          grossvalue: Number(item.grossvalue) || 0,
          discountrate: Number(item.discountrate) || 0,
          discountvalue: Number(item.discountvalue) || 0,
          perunitdiscountvalue: Number(processed?.perunitdiscountvalue) || 0, //new
          taxableamount: Number(processed?.taxableamount) || 0, //new
          perunittaxableamount: Number(processed?.perunittaxableamount) || 0, //new
          taxrate: Number(item.taxrate) || 0,
          taxvalue: Number(processed?.taxvalue) || 0,
          perunittaxvalue: Number(processed?.perunittaxvalue) || 0, //new
          aftertaxvalue: Number(processed?.aftertaxvalue) || 0, //new
          perunitaftertaxvalue: Number(processed?.perunitaftertaxvalue) || 0, //new
          batchno: item.batchno || "",
          serialno: item.serialno || "",
          manufacdate: item.manufacdate || CON_DEFAULT_DATE,
          expdate: item.expdate || CON_DEFAULT_DATE,
          podate: item.podate || CON_DEFAULT_DATE,
          descrip: item.descrip || "",
          reason: item.reason || "",
          dtoDOCs:
            attachments.length > 0
              ? attachments.map((att) => {
                  const { docpathjson, bytarr } = docPayloadField(att.docpathjson || att.bytarr || "");
                  return {
                    doctypecode: String(att.doctypecode) || "0",
                    doctype: att.doctype || "",
                    docpathjson,
                    doctypeothername: att.doctypeothername || "",
                    filename: docpathjson || "",
                    bytarr,
                    remark: att.remark || "",
                  };
                })
              : [
                  {
                    doctypecode: "0",
                    docpathjson: "",
                    filename: "",
                    doctypeothername: "",
                    bytarr: "",
                    remark: "",
                  },
                ],
        };
      });

    const productReturns = grnData?.products.map((item) => {
      const returnedqty = Number(item.rejectItem?.returnedqty) || 0;

      return {
        supproductcode: String(item.supproductcode) || 0,
        returnedqty,
        descrip: item.rejectItem?.descrip || "",
        reason: item.rejectItem?.reason || item.rejectItem?.descrip,
        dtoDOCs:
          Array.isArray(item.rejectItem?.attachments) &&
          item.rejectItem.attachments.length > 0
            ? item.rejectItem.attachments.map((att) => {
                const { docpathjson, bytarr } = docPayloadField(att.docpathjson || att.bytarr || "");
                return {
                  doctypecode: "1",
                  docpathjson,
                  filename: docpathjson || "",
                  doctypeothername: att.doctypeothername || "",
                  bytarr,
                  remark: att.remark || "",
                };
              })
            : [
                {
                  doctypecode: "0",
                  docpathjson: "",
                  doctypeothername: "",
                  filename: "",
                  bytarr: "",
                  remark: "",
                },
              ],
      };
    });

    const productresDocs =
      Array.isArray(resattachments) && resattachments.length > 0
        ? resattachments.map((att) => {
            const { docpathjson, bytarr } = docPayloadField(att.docpathjson || att.bytarr || "");
            return {
              doctypecode: String(att.doctypecode) || "",
              docpathjson,
              doctypeothername: String(att.doctypeothername) || "",
              filename: docpathjson || "",
              bytarr,
              remark: att.docremark || "",
            };
          })
        : [
            {
              doctypecode: "0",
              docpathjson: "",
              filename: "",
              doctypeothername: "",
              bytarr: "",
              remark: "",
            },
          ];

    const productDocs =
      Array.isArray(attachments) && attachments.length > 0
        ? attachments.map((att) => {
            const { docpathjson, bytarr } = docPayloadField(att.docpathjson || att.bytarr || "");
            return {
              doctypecode: String(att.doctypecode) || "",
              docpathjson,
              doctypeothername: att.doctypeothername || "",
              filename: docpathjson || "",
              bytarr,
              remark: att.remark || att.docremark || "",
            };
          })
        : [
            {
              doctypecode: "0",
              docpathjson: "",
              filename: "",
              doctypeothername: "",
              bytarr: "",
              remark: "",
            },
          ];
    console.log("submitGrnData - productDocs (GRN attachments):", productDocs);

    // Check if products have actual non-zero taxes applied
    const hasProductLevelTaxesSubmit =
      Array.isArray(grnData?.taxes) &&
      grnData.taxes.some((tax) => Number(tax.amount) > 0);

    // Check if bill-level tax is being used
    const isBillLevelTaxAppliedSubmit =
      grnData?.billings?.billLevelTaxApplied === true ||
      (grnData?.billings?.billLevelTaxPercent !== undefined &&
        grnData?.billings?.billLevelTaxPercent !== null &&
        !hasProductLevelTaxesSubmit);

    const paymentTax =
      // CASE 1: If products have actual non-zero taxes → use product-level taxes
      hasProductLevelTaxesSubmit
        ? grnData.taxes.map((tax) => ({
            taxcode: "0",
            taxrate: Number(tax.taxrate) || 0,
            taxvalue: Number(tax.amount) || 0,
          }))
        : // CASE 2: If bill-level tax is applied (even if 0%)
        isBillLevelTaxAppliedSubmit
        ? [
            {
              taxcode: "1",
              taxrate: Number(grnData?.billings?.billLevelTaxPercent) || 0,
              taxvalue: Number(grnData?.billings?.vatAmount) || 0,
            },
          ]
        : // CASE 3: Fallback default
          [
            {
              taxcode: "0",
              taxrate: 0,
              taxvalue: 0,
            },
          ];
    console.log("paymentTax payload in ProcessGrnApi:", paymentTax);
    console.log("paymentTax payload:", paymentTax);

    const paymentCharges =
      Array.isArray(supcharges) && supcharges.length > 0
        ? supcharges.map((charge) => ({
            supchargetypecode: charge.supchargetypecode || "",
            chargerate: parseFloat(charge.chaegerate) || 0,
            chargevalue: parseFloat(charge.chargevalue) || 0,
          }))
        : [
            {
              supchargetypecode: "1",
              chargerate: 0,
              chargevalue: 0,
            },
          ];
    const processedcharge = calSummary[0]?.dtCalCharges || [];

    console.log("processedcharge:", calSummary?.dtCalCharges); // Log the result of find

    const Charges = processedcharge.map((processedchar) => ({
      chargecode: processedchar.chargecode || "0",
      supproductcode: processedchar.supproductcode || "",
      chargevalue: processedchar.chargevalue || 0,
      perunitchargevalue: processedchar.perunitchargevalue || 0,
    }));

    const {
      warehouse,
      partialShipments,
      deliveryDate,
      poNumber,
      poDate,
      invoiceNumber,
      invoiceDate,
      dispatchNumber,
      dispatchDate,
      awbNumber,
      billOfLandingNumber,
      deliveryNoteNumber,
    } = grnDetailsForm;

    const payload = {
      dtProducts: products,
      dtProductReturns: productReturns,
      dtoDOCs: productDocs,
      dtoReasonDOCs: productresDocs,

      dtPaymentCharges: paymentCharges,
      dtoCharges: Charges,

      dtPaymentTax: paymentTax,
      grncode: grncode || "",
      compcode: COMPCODE || "",
      loccode: STAFFMAPLOCODE || "",
      suprefcode: selectedSupplier?.optSupplierRefCode || "",
      restcode: warehouse || "",
      ponumber: poNumber || "",
      podate: FormatDateShort(poDate) || "",
      invoiceno: invoiceNumber || "",
      invoicedate: FormatDateShort(invoiceDate) || "",
      dispatchno: dispatchNumber ? String(dispatchNumber) : "",
      dispatchedate: FormatDateShort(dispatchDate) || "",
      deliverynoteno: deliveryNoteNumber ? String(deliveryNoteNumber) : "",
      deliverynotedate: FormatDateShort(deliveryDate) || "",
      loccodeto: String(warehouse) || "",
      blno: billOfLandingNumber ? String(billOfLandingNumber) : "",
      // bulkdiscountrate: 0,
      bulkdiscountrate: grnData?.billings?.billLevelDiscountApplied
        ? Number(grnData.billings?.billLevelDiscountPercent || 0)
        : 0,
      totbulkdiscount: grnData?.billings?.billLevelDiscountApplied
        ? Number(grnData.billings?.discountAmount || 0)
        : 0,

      totpenalty: 0,
      ispartialship: partialShipments === "Y" ? "Y" : "N",
      awbno: awbNumber ? String(awbNumber) : "",
      descrip: remark || "",
      reason: reason || "",
      enuser: USERNAME || "",
      sts,
      trsts: trsts.toString(),
      businesstypecode: CON_BUSINESS_TYPECODE,

      // Submit/Hold action fields when creating GRN
      // reasonfor: reasonfor || "",
      // remark: remark || "",
      // attachments: attachments || "",
    };
    console.log("GRN payload", payload);
    // console.log("GRN createGrnApi payload", paymentCharges);
    const response = await createGrnApi(payload);

    if (onSuccess) onSuccess(response);
  } catch (error) {
    if (onError) onError(error);
  }
}
// Grn Basic Edit
export async function UpdateGrnBasic(payload: AnyForNow): Promise<AnyForNow> {
  try {
    const response = await externalpostApi(GRN_API_06, payload);
    return response;
  } catch (error) {
    throw error;
  }
}
export async function UpdateGrnBasicData({
  grnDetailsForm,
  selectedSupplier,
  grncode,
  onSuccess,
  onError,
  sts = CON_GRN_STATUS_HOLD,
  COMPCODE,
  STAFFMAPLOCODE,
  USERNAME,
  pocode,
  trsts = CON_GRN_STATUS_SAVED,
}: UpdateGrnBasicDataParams): Promise<void> {
  try {
    const {
      warehouse,
      partialShipments,
      deliveryDate,
      poNumber,
      poDate,
      invoiceNumber,
      invoiceDate,
      dispatchNumber,
      dispatchDate,
      awbNumber,
      billOfLandingNumber,
      deliveryNoteNumber,
    } = grnDetailsForm;

    const payload = {
      grncode: String(grncode) || "",
      pocode: String(pocode) || "",
      compcode: COMPCODE || "",
      loccode: STAFFMAPLOCODE || "",
      suprefcode: selectedSupplier?.optSupplierRefCode || "",
      ponumber: poNumber || "",
      podate: FormatDateShort(poDate) || "",
      invoiceno: invoiceNumber || "",
      invoicedate: FormatDateShort(invoiceDate) || "",
      dispatchno: dispatchNumber ? String(dispatchNumber) : "",
      dispatchedate: FormatDateShort(dispatchDate) || "",
      deliverynoteno: deliveryNoteNumber ? String(deliveryNoteNumber) : "",
      deliverynotedate: FormatDateShort(deliveryDate) || "",
      loccodeto: String(warehouse) || "",
      blno: billOfLandingNumber ? String(billOfLandingNumber) : "",
      bulkdiscountrate: 0,
      totbulkdiscount: 0,
      totpenalty: 0,
      ispartialship: partialShipments === "Y" ? "Y" : "N",
      restcode: warehouse || "",
      awbno: awbNumber ? String(awbNumber) : "",
      descrip: "",
      reason: "",
      enuser: USERNAME || "",
      sts,
      trsts,
    };
    console.log("GRN Update Grn Basic payload", payload);

    const response = await UpdateGrnBasic(payload);
    console.log("GRN Update Grn Basic response", response);

    if (onSuccess) onSuccess(response);
  } catch (error) {
    if (onError) onError(error);
  }
}

// Grn product Edit

export async function UpdateGrnProduct(payload: AnyForNow): Promise<AnyForNow> {
  try {
    const response = await externalpostApi(GRN_API_08, payload);
    return response;
  } catch (error) {
    throw error;
  }
}

export async function UpdateGrnProductData({
  grnItems,
  onSuccess,
  onError,
  grncode,
  USERNAME,
  COMPCODE,
  STAFFMAPLOCODE,
  attachments,
  grnData,
  charges,
  addedCharges,
  payTax,
  calSummary,
  sts = CON_GRN_STATUS_SAVED,
  trsts = CON_GRN_STATUS_SAVED,
}: UpdateGrnProductDataParams): Promise<void> {
  try {
    const products = (grnData?.products || [])
      .filter((item) => item.isAdded === true) //  Only include added items
      .map((item) => {
        console.log("Debugging calSummary:", calSummary); // Log calSummary
        console.log("Debugging dtCalProducts:", calSummary?.dtCalProducts); // Log dtCalProducts
        console.log("Debugging item.supproductcode:", item.supproductcode); // Log supproductcode

        const processed = calSummary[0]?.dtCalProducts?.find(
          (p) => String(p.supproductcode) === String(item.supproductcode)
        );

        console.log("processed:", processed); // Log the result of find

        console.log("item.docpathjson:", item.docpathjson);
        return {
          supproductcode: Number(item.supproductcode) || 0,
          poqty: Number(item.poqty) || 0,
          receivedqty: Number(item.receivedqty) || 0,
          acceptedqty: Number(item.acceptedqty) || 0,
          freeqty: Number(item.freeqty) || 0,
          billqty: Number(processed?.billqty) || 0,
          rejectedqty: Number(item.rejectItem?.returnedqty) || 0, //new
          totalqty: Number(processed?.totalqty) || 0, //new
          pendingpoqty: Number(item.pendingpoqty) || 0, //new
          // differenceqty: parseInt(item.differenceqty, 10) || 0,
          unitprice: Number(item.unitprice) || 0,
          grossvalue: Number(item.grossvalue) || 0,
          discountrate: Number(item.discountrate) || 0,
          discountvalue: Number(item.discountvalue) || 0,
          taxrate: Number(item.taxrate) || 0,
          taxvalue: Number(processed?.taxvalue) || 0,
          batchno: item.batchno || "",
          manufacdate: item.manufacdate || CON_DEFAULT_DATE,
          expdate: item.expdate || CON_DEFAULT_DATE,
          podate: item.podate || CON_DEFAULT_DATE,
          descrip: item.descrip || "", //is this description for free item remark?
          serialno: item.serialno || "",
          

          unitpricewithfree: Number(processed?.unitpricewithfree) || 0, //new
          unitdistribution: Number(processed?.unitdistribution) || 0, //new
          perunitdiscountvalue: Number(processed?.perunitdiscountvalue) || 0, //new
          taxableamount: Number(processed?.taxableamount) || 0, //new
          perunittaxableamount: Number(processed?.perunittaxableamount) || 0, //new
          perunittaxvalue: Number(processed?.perunittaxvalue) || 0, //new
          aftertaxvalue: Number(processed?.aftertaxvalue) || 0, //new
          perunitaftertaxvalue: Number(processed?.perunitaftertaxvalue) || 0, //new

          dtoDOCs: (() => {
            // Prefer freshly edited item-level attachments when present
            const rawItemAttachments = Array.isArray(item.itemAttachments)
              ? item.itemAttachments
              : Array.isArray(item.itemAttachments?.attachments)
              ? item.itemAttachments.attachments
              : item.itemAttachments
              ? [item.itemAttachments]
              : [];
            const normalizedItemAttachments: AnyForNow[] = (
              rawItemAttachments || []
            ).map((att) => att as AnyForNow);

            const validEditedAttachments = normalizedItemAttachments.filter((att) => {
              const hasFileData =
                (att?.docpathjson && String(att.docpathjson).trim() !== "") ||
                (att?.FILEPATH && String((att as AnyForNow).FILEPATH).trim() !== "") ||
                (att?.fileUrl && String((att as AnyForNow).fileUrl).trim() !== "") ||
                (att?.objectUrl &&
                  String((att as AnyForNow).objectUrl).trim() !== "") ||
                (att?.bytarr && String((att as AnyForNow).bytarr).trim() !== "");
              const hasName =
                att?.filename ||
                (att as AnyForNow)?.fileName ||
                (att as AnyForNow)?.FILENAME ||
                (att as AnyForNow)?.FILEPATH;
              return hasFileData || hasName;
            });

            if (validEditedAttachments.length > 0) {
              return validEditedAttachments.map((att) => ({
                doctypecode: String(
                  att.doctypecode || (att as AnyForNow)?.DOCTYPECODE || "0"
                ),
                docpathjson:
                  (typeof att.docpathjson === "string" && att.docpathjson) ||
                  (att as AnyForNow)?.FILEPATH ||
                  (att as AnyForNow)?.fileUrl ||
                  (att as AnyForNow)?.objectUrl ||
                  "",
                doctypeothername:
                  att.doctypeothername || (att as AnyForNow)?.DOCTYPEOTHERNAME || "",
                filename:
                  (typeof att.docpathjson === "string" && att.docpathjson) ||
                  (att as AnyForNow)?.FILEPATH ||
                  (att as AnyForNow)?.fileUrl ||
                  (att as AnyForNow)?.objectUrl ||
                  "",
                bytarr:
                  (typeof att.bytarr === "string" && att.bytarr.trim() !== ""
                    ? att.bytarr
                    : "") 
                ,
                remark:
                  att.remark ||
                  (att as AnyForNow)?.REMARK ||
                  (att as AnyForNow)?.docremark ||
                  "",
              }));
            }

            if (item.docpathjson) {
              try {
                const data =
                  typeof item.docpathjson === "string"
                    ? JSON.parse(item.docpathjson)
                    : Array.isArray(item.docpathjson)
                    ? item.docpathjson
                    : [];
                return (data || []).map((doc) => ({
                  doctypecode: String(doc.DOCTYPECODE || "0"),
                  docpathjson: doc.FILEPATH || "",
                  doctypeothername: doc.DOCTYPEOTHERNAME || "",
                  filename: doc.FILENAME || "",
                  bytarr: doc.bytarr,
                  remark: doc.REMARK || "",
                }));
              } catch (e) {
                console.error("Error parsing item.docpathjson:", e);
              }
            }

            if (attachments.length > 0) {
              return attachments.map((att) => ({
                doctypecode: String(att.doctypecode) || "0",
                docpathjson: (typeof att.docpathjson === "string" && att.docpathjson) || "",
                doctypeothername: att.doctypeothername || "",
                filename: (typeof att.docpathjson === "string" && att.docpathjson) || "",
                bytarr:
                  (typeof att.bytarr === "string" && att.bytarr.trim() !== ""
                    ? att.bytarr
                    : "") 
                ,
                remark:
                  att.remark ||
                  (Array.isArray(item.itemAttachments)
                    ? ""
                    : item.itemAttachments?.remark || ""),
              }));
            }

            return [
              {
                doctypecode: "0",
                docpathjson: "",
                doctypeothername: "",
                filename: "",
                bytarr: "",
                remark: "",
              },
            ];
          })(),
        };
      });

    const productReturns = grnData?.products.map((item) => {
      console.log(
        "item.rejectItem?.attachments:",
        item.rejectItem?.attachments
      );

      const attachments = item.rejectItem?.attachments
        ? Array.isArray(item.rejectItem.attachments)
          ? item.rejectItem.attachments
          : [item.rejectItem.attachments]
        : [];

      // Filter out empty/invalid attachments before saving
      const validAttachments = attachments.filter((att) => {
        // Only include attachments that have valid file data
        const hasValidFileData =
          (att?.docpathjson && att.docpathjson.trim() !== "") ||
          (att?.FILEPATH && att.FILEPATH.trim() !== "") ||
          (att?.fileUrl && att.fileUrl.trim() !== "") ||
          (att?.bytarr && att.bytarr.trim && att.bytarr.trim() !== "");
        const hasFileName = att?.FILENAME || att?.filename;
        return hasValidFileData || hasFileName;
      });

      return {
        supproductcode: String(item.supproductcode) || "",
        returnedqty: Number(item.rejectItem?.returnedqty) || 0,
        descrip: item.rejectItem?.descrip || "",
        reason: item.rejectItem?.reason || "",

        dtoDOCs:
          validAttachments.length > 0
            ? validAttachments.map((att) => ({
                doctypecode: att.DOCTYPECODE || att.doctypecode || "1",
                docpathjson:
                  (typeof att.FILEPATH === "string" && att.FILEPATH) ||
                  (typeof att.docpathjson === "string" && att.docpathjson) ||
                  "",
                doctypeothername:
                  att.DOCTYPEOTHERNAME || att.doctypeothername || "",
                filename: (typeof att.FILEPATH === "string" && att.FILEPATH) ||
                  (typeof att.docpathjson === "string" && att.docpathjson) ||
                  "",
                bytarr:
  typeof att.bytarr === "string" && att.bytarr.trim() !== ""
    ? att.bytarr
    : "",
                remark: att.REMARK || att.remark || "",
              }))
            : [], // Return empty array instead of empty object when no attachments
      };
    });

    // Check if products have actual non-zero taxes applied
    const hasProductLevelTaxes =
      Array.isArray(grnData?.taxes) &&
      grnData.taxes.some((tax) => Number(tax.amount) > 0);

    // Check if bill-level tax is being used (billLevelTaxApplied flag or billLevelTaxPercent is explicitly set)
    const isBillLevelTaxApplied =
      grnData?.billings?.billLevelTaxApplied === true ||
      (grnData?.billings?.billLevelTaxPercent !== undefined &&
        grnData?.billings?.billLevelTaxPercent !== null &&
        !hasProductLevelTaxes);

    const paymentTax =
      // PRIORITY 1: If products have actual non-zero taxes → use product-level taxes
      hasProductLevelTaxes
        ? grnData.taxes.map((tax) => ({
            taxcode: "0",
            taxrate: Number(tax.taxrate) || 0,
            taxvalue: Number(tax.amount) || 0,
          }))
        : // PRIORITY 2: If bill-level tax is applied (even if 0%)
        isBillLevelTaxApplied
        ? [
            {
              taxcode: "1",
              taxrate: Number(grnData?.billings?.billLevelTaxPercent) || 0,
              taxvalue: Number(grnData?.billings?.vatAmount) || 0,
            },
          ]
        : // PRIORITY 3: If payTax exists from previously saved data
        payTax && payTax.length > 0
        ? [
            {
              taxcode: payTax?.[0]?.taxcode || "0",
              taxrate: Number(payTax?.[0]?.taxrate) || 0,
              taxvalue: Number(payTax?.[0]?.taxvalue) || 0,
            },
          ]
        : // PRIORITY 4: Fallback
          [
            {
              taxcode: "0",
              taxrate: 0,
              taxvalue: 0,
            },
          ];

    const paymentCharges =
      Array.isArray(charges) && charges.length > 0
        ? charges.map((charge) => ({
            supchargetypecode: charge.supchargetypecode || "",
            chargerate: Number(charge.chargerate || charge.chaegerate) || 0,
            chargevalue: Number(charge.chargerate || charge.chaegerate) || 0,
            supchargetype: charge.supchargetype || "",
            supchargecode: charge.supchargecode || "",
          }))
        : [
            {
              supchargetypecode: "1",
              chargerate: 0,
              chargevalue: 0,
              supchargetype: "",
              supchargecode: "",
            },
          ];
    const processedcharge = calSummary[0]?.dtCalCharges || [];

    console.log("processedcharge:", calSummary[0]?.dtCalCharges); // Log the result of find

    const Charges = processedcharge.map((processedchar) => ({
      chargecode: processedchar.chargecode || "0",
      supproductcode: processedchar.supproductcode || "",
      chargevalue: processedchar.chargevalue || 0,
      perunitchargevalue: processedchar.perunitchargevalue || 0,
    }));

    const payload = {
      dtProducts: products,
      dtProductReturns: productReturns,
      dtPaymentCharges: paymentCharges,
      dtPaymentTax: paymentTax,
      dtoCharges: Charges,

      grncode: String(grncode) || "",
      compcode: COMPCODE || "",
      loccode: STAFFMAPLOCODE || "",
      businesstypecode: CON_BUSINESS_TYPECODE,

      bulkdiscountrate: grnData?.billings?.billLevelDiscountApplied
        ? Number(grnData.billings?.billLevelDiscountPercent || 0)
        : 0,
      totbulkdiscount: grnData?.billings?.billLevelDiscountApplied
        ? Number(grnData.billings?.discountAmount || 0)
        : 0,
      totpenalty: 0,

      descrip: "",
      enuser: USERNAME || "",
      sts,
      trsts,
    };
    console.log("UpdateGrnProduct payload", payload);

    const response = await UpdateGrnProduct(payload);

    if (onSuccess) onSuccess(response);
  } catch (error) {
    if (onError) onError(error);
  }
}

// Grn Attachment Edit
export async function UpdateGrnDocs(payload: AnyForNow): Promise<AnyForNow> {
  try {
    const response = await externalpostApi(GRN_API_07, payload);
    return response;
  } catch (error) {
    throw error;
  }
}

export async function UpdateGrnDocsData({
  attachments,
  onSuccess,
  onError,
  grncode,
  grndoccode,
  USERNAME,
  COMPCODE,
  STAFFMAPLOCODE,
  sts = CON_GRN_STATUS_SAVED,
  trsts = CON_GRN_STATUS_SAVED,
}: UpdateGrnDocsDataParams): Promise<void> {
  try {
    console.log("GRN Update Docs Attachments", attachments);
    const productDocs = attachments.map((item) => ({
      doctypecode: String(item.doctypecode) || "",
      docpathjson: (typeof item.docpathjson === "string" && item.docpathjson) || "",
      filename: item.filename || item.fileName || "",
      doctypeothername: item.doctypeothername || "",
      bytarr:"",
     
      remark: item.remark || "",
    }));

    const payload = {
      compcode: COMPCODE || "",
      businesstypecode: CON_BUSINESS_TYPECODE || "",
      grndoccode: String(grndoccode) || "",
      grncode: String(grncode) || "",
      descrip: "",
      enuser: USERNAME || "",
      sts,
      trsts,
      productDocs,
    };
    console.log("UpdateGrnDocsData payload", payload);

    const response = await UpdateGrnDocs(payload);

    if (onSuccess) onSuccess(response);
  } catch (error) {
    if (onError) onError(error);
  }
}

export async function DeleteGrnDocsData({
  attachments,
  onSuccess,
  onError,
  grncode,
  grndoccode,
  USERNAME,
  COMPCODE,
  STAFFMAPLOCODE,
  sts = CON_GRN_STATUS_SAVED,
}: Omit<UpdateGrnDocsDataParams, "trsts">): Promise<void> {
  try {
    const productDocs = [
      {
        doctypecode: "0",
        docpathjson: "0",
        filename: "0",
        bytarr: "JVBERi0xLjQKJfbk/N8KMSAwIG9iago8PAovVHlwZSAv",
        remark: "0",
        doctypeothername: "0",
      },
    ];

    const payload = {
      compcode: COMPCODE || "",
      businesstypecode: CON_BUSINESS_TYPECODE || "",
      grndoccode: String(grndoccode) || "",
      grncode: String(grncode) || "",
      descrip: "",
      enuser: USERNAME || "",
      sts,
      productDocs,
    };
    console.log("UpdateGrnDocsData payload", payload);

    const response = await UpdateGrnDocs(payload);

    if (onSuccess) onSuccess(response);
  } catch (error) {
    if (onError) onError(error);
  }
}

// Grn Updategrn upto now below function no need
export async function UpdateGrn(payload: AnyForNow): Promise<AnyForNow> {
  try {
    const response = await externalpostApi(GRN_API_05, payload);
    return response;
  } catch (error) {
    throw error;
  }
}

export async function UpdateGrnData({
  grnItems,
  grnDetailsForm,
  selectedSupplier,
  onSuccess,
  onError,
  rejectItems,
  attachments,
  sts = CON_GRN_STATUS_SAVED,
  trsts = CON_GRN_STATUS_SAVED,
  grnData,
}: AnyForNow): Promise<void> {
  try {
    const products = grnItems.map((item) => ({
      supproductcode: parseInt(item.supproductcode, 10) || 0,
      poqty: parseFloat(item.poqty) || 0,
      receivedqty: parseFloat(item.receivedqty) || 0,
      acceptedqty: parseFloat(item.acceptedqty) || 0,
      freeqty: parseFloat(item.freeqty) || 0,
      billqty: parseFloat(item.billqty) || 0,
      // differenceqty: parseInt(item.differenceqty, 10) || 0,
      unitprice: parseFloat(item.unitprice) || 0,
      grossvalue: parseFloat(item.grossvalue) || 0,
      discountrate: parseFloat(item.discountrate) || 0,
      discountvalue: parseFloat(item.discountvalue) || 0,
      taxrate: parseFloat(item.taxrate) || 0,
      taxvalue: parseFloat(item.taxvalue) || 0,
      batchno: item.batchno || "",
      manufacdate: item.manufacdate || CON_DEFAULT_DATE,
      // expirydate: item.expirydate || "2025-05-21",
      podate: item.podate || CON_DEFAULT_DATE,
      descrip: item.descrip || "", //is this description for free item remark?

      // doctype: String(item.doctype) || "",
      // docname: att.docname || "", only if document type is Other need to enter Document Type name
      // docremark: att.docremark || "",
      // docpathjson: item.docpathjson || "",
    }));
    const productReturns = grnItems.map((item) => ({
      supproductcode: String(item.supproductcode) || "",
      returnedqty: parseInt(item.rejectItem?.returnedqty, 10) || 0,
      // returnedreason: item.returnedreason || "",
      descrip: item.rejectItem?.descrip || "",
      reason: item.rejectItem?.reason || item.reason || "",
      docpathjson: item.rejectItem?.docpathjson || "",
    }));

    const productDocs =
      Array.isArray(attachments) && attachments.length > 0
        ? attachments.map((att) => ({
            doctypecode: String(att.doctypecode) || "",
            // docname: att.docname || "", only if document type is Other need to enter Document Type name
            // docdesc: att.docdesc || "",
            docpathjson: att.docpathjson || "",
            doctypeothername: att.doctypeothername || "",
            remark: att.remark || "",
          }))
        : [
            {
              doctypecode: "",
              // docname: "", // if required
              // docdesc: "", // if required
              docpathjson: "",
              doctypeothername: "",
              remark: "",
            },
          ];

    const {
      COMPCODE,
      STAFFMAPLOCODE,
      USERNAME,
      warehouse,
      partialShipments,
      deliveryDate,
      poNumber,
      poDate,
      invoiceNumber,
      invoiceDate,
      dispatchNumber,
      dispatchDate,
      awbNumber,
      billOfLandingNumber,
      deliveryNoteNumber,
    } = grnDetailsForm;

    const payload = {
      dtProducts: products || [],
      dtProductReturns: productReturns || [],
      dtoProductDOCs: productDocs,

      dtPaymentCharges: [
        {
          supchargetypecode: "1",
          chargerate: 0,
          chargevalue: 0,
        },
      ],
      dtPaymentTax: [
        {
          taxcode: "1", // <-- changed from 'unknown' to ""
          taxrate: 0,
          taxvalue: 0,
        },
      ],
      grncode: "",
      compcode: COMPCODE || "",
      loccode: STAFFMAPLOCODE || "",
      suprefcode: selectedSupplier?.optSupplierRefCode || "",
      ponumber: poNumber || "",
      podate: FormatDateShort(poDate) || "",
      invoiceno: invoiceNumber || "",
      invoicedate: FormatDateShort(invoiceDate) || "",
      dispatchno: dispatchNumber ? String(dispatchNumber) : "",
      dispatchedate: FormatDateShort(dispatchDate) || "",
      deliverynoteno: deliveryNoteNumber ? String(deliveryNoteNumber) : "",
      deliverynotedate: FormatDateShort(deliveryDate) || "",
      loccodeto: String(warehouse) || "",
      blno: billOfLandingNumber ? String(billOfLandingNumber) : "",
      bulkdiscountrate: 0,
      totbulkdiscount: 0,
      totpenalty: 0,
      // ispartialship: partialShipments === 10 ? "Y" : "N",
      ispartialship: partialShipments === "Y" ? "Y" : "N",
      restcode: warehouse || "",
      awbno: awbNumber ? String(awbNumber) : "",
      descrip: "",
      enuser: USERNAME || "",
      sts,
      trsts,

      // Submit/Hold action fields when creating GRN
      // reasonfor: reasonfor || "",
      // remark: remark || "",
      // attachments: attachments || "",
    };
    console.log("GRN payload", payload);

    const response = await createGrnApi(payload);

    if (onSuccess) onSuccess(response);
  } catch (error) {
    if (onError) onError(error);
  }
}

export async function ProcessGrnApi({
  USERNAME,
  COMPCODE,
  RESTCODE,
  STAFFMAPLOCODE,
  grncode,
  grnData,
  addedCharges,
  onSuccess,
  onError,
  payTax,
}: ProcessGrnApiParams): Promise<void> {
  try {
    console.log("========== ProcessGrnApi CALLED ==========");
    console.log(
      "addedCharges received:",
      JSON.stringify(addedCharges, null, 2)
    );
    console.log(
      "grnData.billings:",
      JSON.stringify(grnData?.billings, null, 2)
    );
    console.log("grnData.products count:", grnData?.products?.length);

    const products = (grnData?.products || [])
      .filter((item) => item.isAdded)
      .map((item) => {
        return {
          supproductcode: Number(item.supproductcode) || 0,
          poqty: Number(item.poqty) || 0,
          receivedqty: Number(item.receivedqty) || 0,
          rejectedqty: Number(item.rejectItem?.returnedqty) || 0, //new
          acceptedqty: Number(item.acceptedqty) || 0,
          freeqty: Number(item.freeqty) || 0,
          billqty: Number(item.billqty) || 0,
          totalqty: Number(item.totalqty) || 0, //new
          pendingpoqty: Number(item.pendingpoqty) || 0, //new
          unitprice: Number(item.unitprice) || 0,
          unitpricewithfree: Number(item.unitpricewithfree) || 0, //new
          unitdistribution: Number(item.unitdistribution) || 0, //new
          grossvalue: Number(item.grossvalue) || 0,
          discountrate: Number(item.discountrate) || 0,
          discountvalue: Number(item.discountvalue) || 0,
          perunitdiscountvalue: Number(item.perunitdiscountvalue) || 0, //new
          taxableamount: Number(item.taxableamount) || 0, //new
          perunittaxableamount: Number(item.perunittaxableamount) || 0, //new
          taxrate: Number(item.taxrate) || 0,
          taxvalue: Number(item.taxvalue) || 0,
          perunittaxvalue: Number(item.perunittaxvalue) || 0, //new
          aftertaxvalue: Number(item.aftertaxvalue) || 0, //new
          perunitaftertaxvalue: Number(item.perunitaftertaxvalue) || 0, //new
          batchno: item.batchno || "",
          manufacdate: item.manufacdate || CON_DEFAULT_DATE,
          expdate: item.expdate || CON_DEFAULT_DATE,
          podate: item.podate || CON_DEFAULT_DATE,
          descrip: item.descrip || "",
        };
      });

    const Charges =
      Array.isArray(addedCharges) && addedCharges.length > 0
        ? addedCharges.flatMap((charge) => {
            const chaegerate =
              Number(charge.chargerate || charge.chaegerate) || 0;

            // Make sure these variables exist in your scope

            const chargevalue = chaegerate;

            console.log("Processing charge:", {
              chargecode: charge.chargecode,
              originalCharge: charge,
              calculatedValue: chargevalue,
            });

            return {
              chargecode: String(charge.chargecode || "0"),
              supproductcode: charge.supproductcode || 0,
              chargerate: chaegerate,
              chargevalue,
              perunitchargevalue: 0,
            };
          })
        : [
            {
              chargecode: "0",
              supproductcode: 0,
              chargerate: 0,
              chargevalue: 0,
              perunitchargevalue: 0,
            },
          ];

    console.log("Final Charges array:", JSON.stringify(Charges, null, 2));

    const paymentTax = [
      {
        taxcode: "0",
        taxrate: 0,
        taxvalue: 0,
      },
    ];

    // Let backend calculate everything fresh - send empty summary
    const dtsummary = [
      {
        totgross: 0,
        tottax: 0,
        totdiscount: 0,
        totcharges: 0,
        totnett: 0,
      },
    ];

    const payload = {
      dtCalProducts: products,
      dtCalCharges: Charges,
      dtCalTax: paymentTax,
      dtCalSummary: dtsummary,
      // grncode: grncode || "",
      compcode: COMPCODE,
      loccode: STAFFMAPLOCODE,
      billdiscountrate:
        Number(grnData?.billings?.billLevelDiscountPercent) || 0,
      billtaxrate: Number(grnData?.billings?.billLevelTaxPercent) || 0,
    };

    console.log("========== FINAL PAYLOAD TO BACKEND ==========");
    console.log(JSON.stringify(payload, null, 2));
    console.log("=============================================");

    const response = await processGrn(payload);
    console.log("========== BACKEND RESPONSE ==========");
    console.log(JSON.stringify(response, null, 2));
    console.log("======================================");

    if (onSuccess) onSuccess(response);
  } catch (error) {
    if (onError) onError(error);
  }
}

// Grn product Edit

export async function processGrn(payload: AnyForNow): Promise<AnyForNow> {
  try {
    const response = await externalpostApi(GRN_API_15, payload);
    return response;
  } catch (error) {
    throw error;
  }
}

export async function ProcessreturnGrnApi({
  USERNAME,
  COMPCODE,
  RESTCODE,
  STAFFMAPLOCODE,
  grncode,
  grnData,
  addedCharges,
  onSuccess,
  onError,
  payTax,
}: ProcessGrnApiParams): Promise<void> {
  try {
    const products = (grnData?.products || [])
      .filter((item) => item.isAdded)
      .map((item) => {
        return {
          supproductcode: Number(item.supproductcode) || 0,
          poqty: Number(item.poqty) || 0,
          receivedqty: Number(item.receivedqty) || 0,
          rejectedqty: Number(item.rejectItem?.returnedqty) || 0, //new
          acceptedqty: Number(item.acceptedqty) || 0,
          freeqty: Number(item.freeqty) || 0,
          billqty: Number(item.billqty) || 0,
          totalqty: Number(item.totalqty) || 0, //new
          pendingpoqty: Number(item.pendingpoqty) || 0, //new
          unitprice: Number(item.unitprice) || 0,
          unitpricewithfree: Number(item.unitpricewithfree) || 0, //new
          unitdistribution: Number(item.unitdistribution) || 0, //new
          grossvalue: Number(item.grossvalue) || 0,
          discountrate: Number(item.discountrate) || 0,
          discountvalue: Number(item.discountvalue) || 0,
          perunitdiscountvalue: Number(item.perunitdiscountvalue) || 0, //new
          taxableamount: Number(item.taxableamount) || 0, //new
          perunittaxableamount: Number(item.perunittaxableamount) || 0, //new
          taxrate: Number(item.taxrate) || 0,
          taxvalue: Number(item.taxvalue) || 0,
          perunittaxvalue: Number(item.perunittaxvalue) || 0, //new
          aftertaxvalue: Number(item.aftertaxvalue) || 0, //new
          perunitaftertaxvalue: Number(item.perunitaftertaxvalue) || 0, //new
          batchno: item.batchno || "",
          manufacdate: item.manufacdate || CON_DEFAULT_DATE,
          expdate: item.expdate || CON_DEFAULT_DATE,
          podate: item.podate || CON_DEFAULT_DATE,
          descrip: item.descrip || "",
        };
      });

    const Charges =
      Array.isArray(addedCharges) && addedCharges.length > 0
        ? addedCharges.flatMap((charge) => {
            const chaegerate =
              Number(charge.chargerate || charge.chaegerate) || 0;

            // Make sure these variables exist in your scope

            const chargevalue = chaegerate;

            return {
              chargecode: charge.chargecode || "",
              supproductcode: charge.supproductcode || "", // assuming charge has supproductcode
              chargerate: chaegerate,
              chargevalue,
              perunitchargevalue: 0,
            };
          })
        : [
            {
              chargecode: 0,
              supproductcode: 0,
              chargerate: 0,
              chargevalue: 0,
              perunitchargevalue: 0,
            },
          ];

    const paymentTax = [
      {
        taxcode: "0",
        taxrate: 0,
        taxvalue: 0,
      },
    ];

    const dtsummary =
      [grnData?.billings].map((bill) => ({
        totgross: 0,
        tottax: 0,
        totdiscount: 0,
        totcharges: 0,
        totnett: 0,
      })) || [];

    const payload = {
      dtCalProducts: products,
      dtCalCharges: Charges,
      dtCalTax: paymentTax,
      dtCalSummary: dtsummary,
      // grncode: grncode || "",
      compcode: COMPCODE,
      loccode: STAFFMAPLOCODE,
      billdiscountrate:
        Number(grnData?.billings?.billLevelDiscountPercent) || 0,
      billtaxrate:
        Number(grnData?.billings?.billLevelTaxPercent) ||
        Number(payTax?.[0]?.taxrate) ||
        0,
    };

    const response = await processreturnGrn(payload);
    console.log("GRN  response", response);

    if (onSuccess) onSuccess(response);
  } catch (error) {
    if (onError) onError(error);
  }
}

export async function processreturnGrn(payload: AnyForNow): Promise<AnyForNow> {
  try {
    const response = await externalpostApi(GRN_API_16, payload);
    return response;
  } catch (error) {
    throw error;
  }
}
